import { MeasureDTO } from '@sales-center/common'

// Formatters
export const fmt = (v: string | number) => {
  if (v == null) return null

  return typeof v === 'number' ? v.toLocaleString() : v
}

export const cFmt = (v: any) => {
  if (v == null) return null

  if (typeof v === 'number') {
    return v.toLocaleString(undefined, { style: 'currency', currency: 'USD' })
  }

  return v
}

// Field mapping type
export type FieldMapping<MeasureDTO> = {
  label: string
  path: (keyof MeasureDTO)[] | string[] // nested path
  formatter?: (value: string | number, obj?: MeasureDTO) => any
}

/* -------------------- Site Location -------------------- */
export const SITE_FIELDS: FieldMapping<MeasureDTO['siteLocation']>[] = [
  {
    label: 'Lot Area (Sq. Ft.)',
    path: ['lot', 'areaSquareFeet'],
    formatter: fmt,
  },
  { label: 'Lot Area (Acres)', path: ['lot', 'areaAcres'], formatter: fmt },
  { label: 'Lot Frontage (Ft)', path: ['lot', 'frontFeet'], formatter: fmt },
  { label: 'Lot Depth (Ft)', path: ['lot', 'depthFeet'], formatter: fmt },
  { label: 'Lot Area Description', path: ['lot', 'areaDescription'] },
  { label: 'Latitude', path: ['parcelCoordinates', 'lat'] },
  { label: 'Longitude', path: ['parcelCoordinates', 'lng'] },
  { label: 'Coordinate Type Code', path: ['parcelCoordinates', 'typeCode'] },
  // Excluded legal description as per request
  //{ label: 'Legal Description', path: ['locationLegal', 'description'] },
  { label: 'Subdivision', path: ['locationLegal', 'subdivisionName'] },
  { label: 'Lot Number', path: ['locationLegal', 'lotNumber'] },
  { label: 'Block Number', path: ['locationLegal', 'blockNumber'] },
  { label: 'Range', path: ['locationLegal', 'range'] },
  { label: 'Township', path: ['locationLegal', 'township'] },
  { label: 'Section', path: ['locationLegal', 'section'] },
  { label: 'Quarter Section', path: ['locationLegal', 'quarterSection'] },
  {
    label: 'Subdivision Tract #',
    path: ['locationLegal', 'subdivisionTractNumber'],
  },
  {
    label: 'Subdivision Plat Book',
    path: ['locationLegal', 'subdivisionPlatBook'],
  },
  {
    label: 'Subdivision Plat Page',
    path: ['locationLegal', 'subdivisionPlatPage'],
  },
  { label: 'Address', path: ['address'] },
  { label: 'City', path: ['city'] },
  { label: 'State', path: ['state'] },
  { label: 'ZIP', path: ['zip'] },
  { label: 'Standardized Address', path: ['standardizedAddress'] },
  { label: 'Standardized City', path: ['standardizedCity'] },
  { label: 'Standardized State', path: ['standardizedState'] },
  { label: 'Standardized ZIP', path: ['standardizedZip'] },
  { label: 'ZIP+4', path: ['standardizedZipPlusFour'] },
  { label: 'State Code', path: ['stateCode'] },
  { label: 'Jurisdiction County Code', path: ['jurisdictionCountyCode'] },
  { label: 'FIPS Code', path: ['fipsCode'] },
  { label: 'Municipality', path: ['municipality'] },
  {
    label: 'Property Type Code',
    path: ['landUseAndZoningCodes', 'propertyTypeCode'],
  },
  {
    label: 'Property Type Description',
    path: ['landUseAndZoningCodes', 'propertyTypeDescription'],
  },
  { label: 'Land Use Code', path: ['landUseAndZoningCodes', 'landUseCode'] },
  {
    label: 'Land Use Description',
    path: ['landUseAndZoningCodes', 'landUseDescription'],
  },
  {
    label: 'State Land Use Code',
    path: ['landUseAndZoningCodes', 'stateLandUseCode'],
  },
  {
    label: 'State Land Use Description',
    path: ['landUseAndZoningCodes', 'stateLandUseDescription'],
  },
  {
    label: 'County Land Use Code',
    path: ['landUseAndZoningCodes', 'countyLandUseCode'],
  },
  {
    label: 'County Land Use Description',
    path: ['landUseAndZoningCodes', 'countyLandUseDescription'],
  },
  { label: 'Zoning Code', path: ['landUseAndZoningCodes', 'zoningCode'] },
  {
    label: 'Zoning Description',
    path: ['landUseAndZoningCodes', 'zoningDescription'],
  },
  { label: 'Census Tract', path: ['censusTract'] },
  { label: 'Mobile Home Indicator', path: ['mobileHomeIndicator'] },
  { label: 'Location Influence Code', path: ['locationInfluenceCode'] },
  {
    label: 'Location Influence Description',
    path: ['locationInfluenceDescription'],
  },
  { label: 'Sewer Type Code', path: ['sewerTypeCode'] },
  { label: 'Sewer Type Description', path: ['sewerTypeDescription'] },
  { label: 'Water Type Code', path: ['waterTypeCode'] },
  { label: 'Water Type Description', path: ['waterTypeDescription'] },
]

/* -------------------- Primary Building -------------------- */
export const BUILDING_FIELDS: FieldMapping<MeasureDTO['buildings'][0]>[] = [
  {
    label: 'Universal Building Area (Sq. Ft.)',
    path: ['universalBuildingAreaSqFt'],
    formatter: fmt,
  },
  {
    label: 'Building Area (Sq. Ft.)',
    path: ['buildingAreaSqFt'],
    formatter: fmt,
  },
  { label: 'Building Area Indicator', path: ['buildingAreaSqFtIndicator'] },
  {
    label: 'Building Area Indicator Description',
    path: ['buildingAreaSqFtIndicatorDescription'],
  },
  { label: 'Living Area (Building)', path: ['livingAreaSqFt'], formatter: fmt },
  { label: 'Ground Floor Area', path: ['groundFloorAreaSqFt'], formatter: fmt },
  { label: 'Gross Area', path: ['grossAreaSqFt'], formatter: fmt },
  { label: 'Adjusted Area', path: ['adjustedAreaSqFt'], formatter: fmt },
  {
    label: 'Basement Area (Sq. Ft.)',
    path: ['basementAreaSqFt'],
    formatter: fmt,
  },
  { label: 'Stories', path: ['storiesCount'] },
  { label: 'Story Type Code', path: ['storyTypeCode'] },
  { label: 'Story Type Description', path: ['storyTypeDescription'] },
  { label: 'Year Built', path: ['yearBuilt'] },
  { label: 'Effective Year Built', path: ['effectiveYearBuilt'] },
  { label: 'Parking Type Code', path: ['parking', 'typeCode'] },
  { label: 'Parking Type Description', path: ['parking', 'typeDescription'] },
  { label: 'Garage Type Code', path: ['parking', 'garageTypeCode'] },
  {
    label: 'Garage Type Description',
    path: ['parking', 'garageTypeDescription'],
  },
  { label: 'Parking Spaces', path: ['parking', 'parkingSpacesCount'] },
  {
    label: 'Garage Primary Area (Sq. Ft.)',
    path: ['parking', 'primaryAreaSqFt'],
    formatter: fmt,
  },
  {
    label: 'Garage Secondary Area (Sq. Ft.)',
    path: ['parking', 'secondAreaSqFt'],
    formatter: fmt,
  },
  { label: 'Pool Area (Sq. Ft.)', path: ['pool', 'areaSqFt'], formatter: fmt },
  { label: 'Pool Type Code', path: ['pool', 'typeCode'] },
  { label: 'Pool Indicator', path: ['pool', 'indicator'] },
  { label: 'Pool Type Description', path: ['pool', 'typeDescription'] },
  { label: 'Patio count', path: ['patios', 'count'] },
  {
    label: 'Patio Area (Sq. Ft.)',
    path: ['patios', 'areaSqFt'],
    formatter: fmt,
  },
  { label: 'Porch count', path: ['porches', 'count'] },
  {
    label: 'Porch Area (Sq. Ft.)',
    path: ['porches', 'areaSqFt'],
    formatter: fmt,
  },
  { label: 'Condition Code', path: ['conditionCode'] },
  { label: 'Condition Description', path: ['conditionDescription'] },
  { label: 'Quality Code', path: ['qualityTypeCode'] },
  { label: 'Quality Description', path: ['qualityTypeDescription'] },
  { label: 'Architecture Code', path: ['architectureTypeCode'] },
  { label: 'Architecture Description', path: ['architectureTypeDescription'] },
  { label: 'Foundation Code', path: ['foundationTypeCode'] },
  { label: 'Foundation Description', path: ['foundationTypeDescription'] },
  { label: 'Construction Code', path: ['constructionTypeCode'] },
  { label: 'Construction Description', path: ['constructionTypeDescription'] },
  { label: 'Exterior Walls Code', path: ['exteriorWallsCode'] },
  { label: 'Exterior Walls Description', path: ['exteriorWallsDescription'] },
  { label: 'Frame Code', path: ['frameTypeCode'] },
  { label: 'Frame Description', path: ['frameTypeDescription'] },
  { label: 'Roof Cover Code', path: ['roofCoverCode'] },
  { label: 'Roof Cover Description', path: ['roofCoverDescription'] },
  { label: 'Roof Type Code', path: ['roofTypeCode'] },
  { label: 'Roof Type Description', path: ['roofTypeDescription'] },
  { label: 'Heating Code', path: ['heatingTypeCode'] },
  { label: 'Heating Description', path: ['heatingTypeDescription'] },
  { label: 'AC Code', path: ['airConditioningTypeCode'] },
  { label: 'AC Description', path: ['airConditioningTypeDescription'] },
  { label: 'Energy Code', path: ['energyTypeCode'] },
  { label: 'Energy Description', path: ['energyTypeDescription'] },
  { label: 'Fuel Code', path: ['fuelTypeCode'] },
  { label: 'Fuel Description', path: ['fuelTypeDescription'] },
  { label: 'View Code', path: ['viewCode'] },
  { label: 'View Description', path: ['viewDescription'] },
  { label: 'Floor Type Code', path: ['floorTypeCode'] },
  { label: 'Floor Type Description', path: ['floorTypeDescription'] },
  { label: 'Fireplace Indicator', path: ['fireplaceIndicator'] },
  { label: 'Fireplaces', path: ['fireplaceCount'] },
  { label: 'Fireplace Type Code', path: ['fireplaceTypeCode'] },
  { label: 'Fireplace Type Description', path: ['fireplaceTypeDescription'] },
  { label: 'Bedrooms', path: ['bedroomsCount'] },
  { label: 'Rooms', path: ['roomsCount'] },
  { label: 'Bathrooms', path: ['bathroomsCount'] },
  { label: 'Full Bathrooms', path: ['fullBathroomsCount'] },
  { label: 'Half Bathrooms', path: ['halfBathroomsCount'] },
  { label: 'Quarter Bathrooms', path: ['quarterBathroomsCount'] },
  { label: 'Three Quarter Bathrooms', path: ['threeQuarterBathroomsCount'] },
  { label: 'Bathroom Fixtures', path: ['bathroomFixturesCount'] },
  { label: 'Bathrooms Source Count', path: ['bathroomsSourceCount'] },
  { label: 'Building Type Code', path: ['buildingTypeCode'] },
  { label: 'Building Type Description', path: ['buildingTypeDescription'] },
  { label: 'Improvement Type Code', path: ['improvementTypeCode'] },
  {
    label: 'Improvement Type Description',
    path: ['improvementTypeDescription'],
  },
  { label: 'Basement Finish Code', path: ['basementFinishCode'] },
  { label: 'Basement Finish Description', path: ['basementFinishDescription'] },
]

/* -------------------- Ownership -------------------- */
export const OWNERSHIP_FIELDS: FieldMapping<MeasureDTO['ownership']>[] = [
  { label: 'Corporate Owner Indicator', path: ['corporateIndicator'] },
  {
    label: 'Owner 1',
    path: ['owner1FirstName'],
    formatter: (v, obj) =>
      `${v ?? ''} ${obj?.owner1LastName ?? ''}`.trim() || null,
  },
  {
    label: 'Owner 2',
    path: ['owner2FirstName'],
    formatter: (v, obj) =>
      `${v ?? ''} ${obj?.owner2LastName ?? ''}`.trim() || null,
  },

  { label: 'Mailing Street', path: ['mailingAddress', 'street'] },
  { label: 'Mailing City', path: ['mailingAddress', 'city'] },
  { label: 'Mailing State', path: ['mailingAddress', 'state'] },
  { label: 'Mailing ZIP', path: ['mailingAddress', 'zip'] },
  { label: 'Mailing Direction', path: ['mailingAddress', 'direction'] },
  { label: 'Mailing Mode', path: ['mailingAddress', 'mode'] },
  { label: 'Mailing Carrier Code', path: ['mailingAddress', 'carrierCode'] },
  { label: 'Mailing Number', path: ['mailingAddress', 'number'] },
  { label: 'Mailing Number Prefix', path: ['mailingAddress', 'numberPrefix'] },
  { label: 'Mailing Number 2', path: ['mailingAddress', 'number2'] },
  { label: 'Mailing Number Suffix', path: ['mailingAddress', 'numberSuffix'] },
  { label: 'Mailing Quadrant', path: ['mailingAddress', 'quadrant'] },
  { label: 'Mailing Unit', path: ['mailingAddress', 'unit'] },
  { label: 'Mailing Opt Out', path: ['mailingAddress', 'optOut'] },
]

/* -------------------- Assessment -------------------- */
export const ASSESSMENT_FIELDS: FieldMapping<MeasureDTO['assessment']>[] = [
  { label: 'Total Value', path: ['totalValue'], formatter: cFmt },
  { label: 'Land Value', path: ['landValue'], formatter: cFmt },
  { label: 'Improvement Value', path: ['improvementValue'], formatter: cFmt },
  { label: 'Total Value Code', path: ['totalValueCode'] },
  { label: 'Land Value Code', path: ['landValueCode'] },
  {
    label: 'Assessed Total Value',
    path: ['assessedTotalValue'],
    formatter: cFmt,
  },
  {
    label: 'Assessed Land Value',
    path: ['assessedLandValue'],
    formatter: cFmt,
  },
  {
    label: 'Assessed Improvement Value',
    path: ['assessedImprovementValue'],
    formatter: cFmt,
  },
  { label: 'Market Total Value', path: ['marketTotalValue'], formatter: cFmt },
  { label: 'Market Land Value', path: ['marketLandValue'], formatter: cFmt },
  {
    label: 'Market Improvement Value',
    path: ['marketImprovementValue'],
    formatter: cFmt,
  },
  {
    label: 'Appraised Total Value',
    path: ['appraisedTotalValue'],
    formatter: cFmt,
  },
  {
    label: 'Appraised Land Value',
    path: ['appraisedLandValue'],
    formatter: cFmt,
  },
  {
    label: 'Appraised Improvement Value',
    path: ['appraisedImprovementValue'],
    formatter: cFmt,
  },
  { label: 'Tax Amount', path: ['taxAmount'], formatter: cFmt },
  { label: 'Tax Year', path: ['taxYear'] },
  { label: 'Assessment Year', path: ['assessmentYear'] },
  { label: 'Tax Area', path: ['taxArea'] },
]

/* -------------------- Last Sale -------------------- */
export const LAST_SALE_FIELDS: FieldMapping<MeasureDTO['lastSale']>[] = [
  { label: 'Last Sale Document #', path: ['documentNumber'] },
  { label: 'Last Sale Book/Page', path: ['bookPage'] },
  { label: 'Last Sale Type', path: ['documentType'] },
  { label: 'Last Sale Type Description', path: ['documentTypeDescription'] },
  { label: 'Recording Date', path: ['recordingDate'] },
]

/* -------------------- Identifiers -------------------- */
export const IDENTIFIER_FIELDS: FieldMapping<MeasureDTO['identifiers']>[] = [
  { label: 'APN', path: ['apn'] },
  { label: 'Formatted APN', path: ['apn2'] },
  { label: 'Uniform APN', path: ['uniformApn'] },
  { label: 'Original APN', path: ['originalApn'] },
  { label: 'Account Number', path: ['accountNumber'] },
]

/* -------------------- Provider Identifiers -------------------- */
export const PROVIDER_IDENTIFIER_FIELDS: FieldMapping<
  MeasureDTO['providerIdentifiers']
>[] = [
  //  excluded provider field as per request
  // { label: 'Data Provider', path: ['provider'] },
  { label: 'Spatial Parcel Point Id', path: ['spatialParcelPointId'] },
  { label: 'Clip', path: ['clip'] },
  { label: 'Previous Clip', path: ['previousClip'] },
  { label: 'Parcel Id', path: ['parcelId'] },
  { label: 'APN Sequence #', path: ['apnSequenceNumber'] },
  { label: 'Formatted APN (Provider)', path: ['formattedApn'] },
  { label: 'Map Reference 1', path: ['mapReference1'] },
  { label: 'Map Reference 2', path: ['mapReference2'] },
  { label: 'Metadata Id', path: ['metadataId'] },
  { label: 'Record Action Indicator', path: ['recordActionIndicator'] },
]

/* -------------------- Buildings Summary -------------------- */
export const BUILDINGS_SUMMARY_FIELDS: FieldMapping<
  MeasureDTO['buildingsSummary']
>[] = [
  { label: 'Building Count', path: ['buildingCount'] },
  { label: 'Units Count', path: ['unitsCount'] },
  { label: 'Summary Living Area', path: ['livingAreaSqFt'], formatter: fmt },
  { label: 'Summary Total Area', path: ['totalAreaSqFt'], formatter: fmt },
  { label: 'Summary Bedrooms', path: ['bedroomsCount'] },
  { label: 'Summary Bathrooms', path: ['bathroomsCount'] },
  { label: 'Summary Full Bathrooms', path: ['fullBathroomsCount'] },
  { label: 'Summary Half Bathrooms', path: ['halfBathroomsCount'] },
]

/* -------------------- Master Map -------------------- */
export const MEASURE_DISPLAY_MAP = {
  siteLocation: SITE_FIELDS,
  buildings: BUILDING_FIELDS,
  ownership: OWNERSHIP_FIELDS,
  assessment: ASSESSMENT_FIELDS,
  lastSale: LAST_SALE_FIELDS,
  identifiers: IDENTIFIER_FIELDS,
  providerIdentifiers: PROVIDER_IDENTIFIER_FIELDS,
  buildingsSummary: BUILDINGS_SUMMARY_FIELDS,
}
